import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const ManagementCapacity = ({ capacity, onCapacityChange }) => {
  const competencyLevelOptions = [
    { value: 'expert', label: 'Ahli (8-10 tahun pengalaman)' },
    { value: 'advanced', label: 'Mahir (5-7 tahun pengalaman)' },
    { value: 'intermediate', label: 'Menengah (3-4 tahun pengalaman)' },
    { value: 'basic', label: 'Dasar (1-2 tahun pengalaman)' },
    { value: 'novice', label: 'Pemula (<1 tahun pengalaman)' }
  ];

  const readinessOptions = [
    { value: 'ready', label: 'Siap (Dapat scale-up segera)' },
    { value: 'nearReady', label: 'Hampir Siap (Perlu 1-3 bulan persiapan)' },
    { value: 'developing', label: 'Berkembang (Perlu 3-6 bulan pengembangan)' },
    { value: 'notReady', label: 'Belum Siap (Perlu >6 bulan pengembangan)' }
  ];

  const competencyAreas = [
    {
      id: 'technical',
      title: 'Kompetensi Teknis',
      icon: 'Wrench',
      description: 'Pengetahuan teknis produksi susu dan operasional pabrik',
      skills: [
        'Proses produksi susu',
        'Kontrol kualitas produk',
        'Pemeliharaan peralatan',
        'Manajemen rantai dingin'
      ]
    },
    {
      id: 'managerial',
      title: 'Kompetensi Manajerial',
      icon: 'Users',
      description: 'Kemampuan kepemimpinan dan manajemen tim',
      skills: [
        'Kepemimpinan tim',
        'Pengambilan keputusan',
        'Manajemen konflik',
        'Delegasi tugas'
      ]
    },
    {
      id: 'financial',
      title: 'Kompetensi Keuangan',
      icon: 'TrendingUp',
      description: 'Pemahaman keuangan dan analisis bisnis',
      skills: [
        'Analisis laporan keuangan',
        'Manajemen cashflow',
        'Budgeting dan forecasting',
        'Cost control'
      ]
    },
    {
      id: 'strategic',
      title: 'Kompetensi Strategis',
      icon: 'Target',
      description: 'Kemampuan perencanaan dan eksekusi strategi',
      skills: [
        'Perencanaan strategis',
        'Analisis pasar',
        'Manajemen risiko',
        'Inovasi bisnis'
      ]
    }
  ];

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-semibold mb-2">Kapasitas Manajemen</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Evaluasi kompetensi dan kesiapan tim manajemen untuk mendukung scale-up operasional
          </p>
        </div>
        <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl bg-primary/10 flex items-center justify-center">
          <Icon name="GraduationCap" size={24} color="var(--color-primary)" />
        </div>
      </div>
      <div className="space-y-8">
        {competencyAreas?.map((area) => (
          <div key={area?.id} className="p-4 md:p-6 bg-muted/30 rounded-lg border border-border">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={area?.icon} size={20} color="var(--color-primary)" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg md:text-xl font-semibold mb-1">{area?.title}</h3>
                <p className="text-sm text-muted-foreground">{area?.description}</p>
              </div>
            </div>

            <div className="space-y-4 mb-4">
              <Select
                label="Level Kompetensi Tim"
                placeholder="Pilih level kompetensi"
                options={competencyLevelOptions}
                value={capacity?.[area?.id]?.level || ''}
                onChange={(value) => onCapacityChange(area?.id, 'level', value)}
                required
              />

              <div className="space-y-2">
                <label className="text-sm font-medium">Keterampilan yang Dikuasai</label>
                {area?.skills?.map((skill) => (
                  <Checkbox
                    key={skill}
                    label={skill}
                    checked={capacity?.[area?.id]?.skills?.includes(skill) || false}
                    onChange={(e) => {
                      const currentSkills = capacity?.[area?.id]?.skills || [];
                      const newSkills = e?.target?.checked
                        ? [...currentSkills, skill]
                        : currentSkills?.filter(s => s !== skill);
                      onCapacityChange(area?.id, 'skills', newSkills);
                    }}
                  />
                ))}
              </div>
            </div>

            <Input
              label="Gap Kompetensi yang Teridentifikasi"
              placeholder="Jelaskan keterampilan atau pengetahuan yang masih kurang"
              value={capacity?.[area?.id]?.gaps || ''}
              onChange={(e) => onCapacityChange(area?.id, 'gaps', e?.target?.value)}
            />
          </div>
        ))}

        <div className="p-4 md:p-6 bg-card rounded-lg border-2 border-primary/20">
          <h3 className="text-lg md:text-xl font-semibold mb-4">Kesiapan Scale-Up Keseluruhan</h3>
          
          <div className="space-y-4">
            <Select
              label="Tingkat Kesiapan Manajemen"
              description="Evaluasi kesiapan tim manajemen secara keseluruhan untuk scale-up"
              placeholder="Pilih tingkat kesiapan"
              options={readinessOptions}
              value={capacity?.overallReadiness || ''}
              onChange={(value) => onCapacityChange('overallReadiness', null, value)}
              required
            />

            <Input
              label="Jumlah Posisi Manajemen Tambahan yang Dibutuhkan"
              type="number"
              placeholder="0"
              min="0"
              description="Estimasi kebutuhan rekrutmen untuk mendukung scale-up"
              value={capacity?.additionalPositions || ''}
              onChange={(e) => onCapacityChange('additionalPositions', null, e?.target?.value)}
              required
            />

            <Input
              label="Rencana Pengembangan Kapasitas"
              placeholder="Jelaskan program training, mentoring, atau rekrutmen yang direncanakan"
              value={capacity?.developmentPlan || ''}
              onChange={(e) => onCapacityChange('developmentPlan', null, e?.target?.value)}
              required
            />
          </div>
        </div>
      </div>
      <div className="mt-6 p-4 bg-success/10 rounded-lg border border-success/20">
        <div className="flex items-start gap-3">
          <Icon name="Lightbulb" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-success-foreground mb-1">Tips Evaluasi Kapasitas</p>
            <p className="text-muted-foreground">
              Fokus pada gap kritis yang dapat menghambat scale-up. Identifikasi kebutuhan training atau rekrutmen dengan prioritas tinggi untuk eksekusi cepat.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagementCapacity;